import { callStreamingEdgeFunction } from './edgeFunctionClient';
import type { ReviewProgress } from './edgeFunctionClient';
import { truncateForAnnotation, truncateForRewrite } from '../utils/documentTruncation';
import { rewriteWithContinuation } from './rewriteContinuation';
import { removeDuplicateSections, regenerateTableOfContents } from '../utils/documentPostProcessor';

export interface Annotation {
  id: string;
  originalText: string;
  newText: string;
  reason: string;
  legalReference: string;
  severity: 'critical' | 'high' | 'medium' | 'low';
  category: string;
}

export interface TenancyAnalysis {
  overallRisk: 'critical' | 'high' | 'medium' | 'low';
  complianceScore: number;
  issueCount: number;
  summary: string;
  issues: Array<{
    title: string;
    description: string;
    severity: 'critical' | 'high' | 'medium' | 'low';
    legalReference: string;
    recommendation: string;
  }>;
  missingProvisions: string[];
  illegalClauses: string[];
  complianceStatus: {
    rentersRightsBill2025: boolean;
    housingAct1988: boolean;
    landlordTenantAct1985: boolean;
    tenantFeesAct2019: boolean;
    homesAct2018: boolean;
    decentHomesStandard: boolean;
  };
}

export interface TenancyReviewResult {
  analysis: TenancyAnalysis;
  rewrittenDocument: string;
  annotations: Annotation[];
}

const DEFAULT_ANALYSIS: TenancyAnalysis = {
  overallRisk: 'high',
  complianceScore: 0,
  issueCount: 0,
  summary: '',
  issues: [],
  missingProvisions: [],
  illegalClauses: [],
  complianceStatus: {
    rentersRightsBill2025: false,
    housingAct1988: false,
    landlordTenantAct1985: false,
    tenantFeesAct2019: false,
    homesAct2018: false,
    decentHomesStandard: false,
  },
};

const ANALYSIS_PROMPT = `You are a senior UK residential property law expert. Analyse the provided tenancy agreement for compliance with ALL of the following legislation:

- Renters' Rights Bill 2025 (abolition of Section 21, periodic tenancies by default, pet provisions, Decent Homes Standard)
- Housing Act 1988 (as amended)
- Landlord and Tenant Act 1985 (repair obligations, fitness for habitation)
- Consumer Rights Act 2015 (unfair terms)
- Homes (Fitness for Human Habitation) Act 2018
- Tenant Fees Act 2019 (prohibited payments)
- Decent Homes Standard (extended to private sector)
- Awaab's Law (damp and mould response timescales)

Return ONLY valid JSON matching this structure:
{
  "overallRisk": "critical|high|medium|low",
  "complianceScore": 0-100,
  "issueCount": number,
  "summary": "brief overall assessment",
  "issues": [
    {
      "title": "short title",
      "description": "detailed explanation",
      "severity": "critical|high|medium|low",
      "legalReference": "specific act/section",
      "recommendation": "what to change"
    }
  ],
  "missingProvisions": ["list of required provisions not present"],
  "illegalClauses": ["list of clauses that are unlawful"],
  "complianceStatus": {
    "rentersRightsBill2025": true/false,
    "housingAct1988": true/false,
    "landlordTenantAct1985": true/false,
    "tenantFeesAct2019": true/false,
    "homesAct2018": true/false,
    "decentHomesStandard": true/false
  }
}

IMPORTANT: Output ONLY the raw JSON object. Do not wrap it in markdown code fences. Do not include any text, commentary, or explanation before or after the JSON.`;

const REWRITE_PROMPT = `You are a senior UK residential property law expert. Rewrite the provided tenancy agreement to be FULLY compliant with ALL current legislation including the Renters' Rights Bill 2025.

PRESERVE all personal details exactly (names, addresses, rent amounts, deposit amounts, dates).

You MUST follow this EXACT section structure. The Table of Contents you write at the top MUST list exactly these sections and no others. Write each section in this exact order. Do NOT create additional sections, do NOT renumber, and do NOT repeat any section topic across different sections.

MANDATORY SECTION OUTLINE:
1. Definitions and Interpretation
2. Grant of Tenancy (periodic tenancy, rolling monthly per 2025 reforms)
3. Rent and Payment Terms
4. Deposit and Protection
5. Tenant's Obligations
6. Landlord's Obligations (include repair, fitness for habitation, gas/electrical safety -- do NOT create separate sections for these)
7. Use of the Property
8. Pets (14-day request/response procedure per Renters' Rights Bill 2025)
9. Alterations and Improvements
10. Insurance
11. Assignment and Subletting
12. Access and Inspections
13. Decent Homes Standard Compliance
14. Awaab's Law -- Damp and Mould Response Timescales
15. Repair Notification and Response Procedures
16. Termination and Section 8 Grounds for Possession (correct notice periods; NO Section 21)
17. Prohibited Fees and Payments (Tenant Fees Act 2019 compliance)
18. Data Protection
19. Notices
20. General Provisions
21. Dispute Resolution
Schedule 1 -- Inventory and Condition
Schedule 2 -- Prescribed Information (Deposit)
EXECUTION -- Signatures (Landlord, Tenant, Guarantor, Witness)

CONTENT RULES:
- REMOVE all Section 21 no-fault eviction provisions
- REMOVE any prohibited fees under Tenant Fees Act 2019
- REMOVE any unfair terms under Consumer Rights Act 2015
- ADD missing provisions identified in the analysis
- Each topic MUST appear in only ONE section -- do NOT duplicate content across sections
- Use professional legal formatting with numbered sub-sections

CRITICAL: You MUST write the COMPLETE document from start to finish, including ALL sections through to the EXECUTION section at the end. The EXECUTION section MUST include signature blocks for THE LANDLORD, THE TENANT(S), THE GUARANTOR, and a WITNESS.

MANDATORY CONTINUATION RULE: You MUST end your response with the exact marker [CONTINUE] unless you have ALREADY written and completed the EXECUTION section containing full signature blocks for all parties. If the last section you wrote is NOT the EXECUTION section, you MUST append [CONTINUE] at the very end of your response. Never omit [CONTINUE] if the document is still incomplete. Never wrap up or summarise early -- keep writing all clauses in full.

Output the complete rewritten agreement as formatted text. Do NOT include any JSON or metadata.`;

const ANNOTATIONS_PROMPT = `You are a legal document comparison expert. Compare the ORIGINAL tenancy agreement with the REWRITTEN version and produce a detailed list of every significant change made.

CRITICAL RULES FOR TEXT FIELDS:
- The "newText" field MUST be an EXACT, VERBATIM substring copied character-for-character from the REWRITTEN DOCUMENT. Do NOT paraphrase, summarize, or shorten. Copy at least 30 characters of contiguous text that appears exactly in the rewritten document. It must be findable using a simple text search (indexOf) in the rewritten document.
- The "originalText" field MUST be an EXACT, VERBATIM substring copied character-for-character from the ORIGINAL DOCUMENT. If the section was newly added (not present in original), set originalText to an empty string "". It must be findable using a simple text search (indexOf) in the original document.
- NEVER paraphrase or summarize text in these fields. Copy the text exactly as it appears.

For each change, provide:
- The exact original text (verbatim from original document)
- The exact new replacement text (verbatim from rewritten document)
- Why the change was made
- The specific legal reference requiring this change
- The severity (critical = unlawful/unenforceable, high = significant compliance gap, medium = best practice improvement, low = minor wording update)
- A category label (e.g., "Section 21 Removal", "Pet Provisions", "Repair Obligations", "Fee Prohibition", "Tenancy Type", "Notice Periods", "Deposit Protection", "Decent Homes", "Awaab's Law")

Return ONLY valid JSON as an array:
[
  {
    "id": "ann_1",
    "originalText": "exact verbatim text from original",
    "newText": "exact verbatim text from rewritten",
    "reason": "explanation of why changed",
    "legalReference": "Act/Section reference",
    "severity": "critical|high|medium|low",
    "category": "category label"
  }
]

IMPORTANT: Output ONLY the raw JSON array. Do not wrap it in markdown code fences. Do not include any text before or after the JSON.`;

function extractJsonByDepth(text: string): string | null {
  const firstBrace = text.indexOf('{');
  const firstBracket = text.indexOf('[');
  let startIdx = -1;
  let openChar = '{';
  let closeChar = '}';

  if (firstBrace === -1 && firstBracket === -1) return null;
  if (firstBrace === -1) { startIdx = firstBracket; openChar = '['; closeChar = ']'; }
  else if (firstBracket === -1) { startIdx = firstBrace; }
  else if (firstBracket < firstBrace) { startIdx = firstBracket; openChar = '['; closeChar = ']'; }
  else { startIdx = firstBrace; }

  let depth = 0;
  let inString = false;
  let escape = false;

  for (let i = startIdx; i < text.length; i++) {
    const ch = text[i];
    if (escape) { escape = false; continue; }
    if (ch === '\\' && inString) { escape = true; continue; }
    if (ch === '"') { inString = !inString; continue; }
    if (inString) continue;
    if (ch === openChar) depth++;
    else if (ch === closeChar) {
      depth--;
      if (depth === 0) return text.slice(startIdx, i + 1);
    }
  }
  return null;
}

function parseJSON<T>(text: string, fallback: T): T {
  try {
    const cleaned = text.replace(/```json\n?/g, '').replace(/```\n?/g, '').trim();
    return JSON.parse(cleaned);
  } catch {
    const extracted = extractJsonByDepth(text);
    if (extracted) {
      try {
        return JSON.parse(extracted);
      } catch {
        // fall through
      }
    }
    return fallback;
  }
}

export interface TenancyReviewOptions {
  onProgress?: (progress: ReviewProgress) => void;
  previousAnalysis?: TenancyAnalysis;
  signal?: AbortSignal;
}

const STAGE_RETRY_DELAY_MS = 3000;

async function retryStage<T>(
  fn: () => Promise<T>,
  signal?: AbortSignal,
): Promise<T> {
  try {
    return await fn();
  } catch (err) {
    if (signal?.aborted) throw err;
    const msg = err instanceof Error ? err.message.toLowerCase() : '';
    const isRetryable = msg.includes('stall') || msg.includes('timed out') ||
      msg.includes('504') || msg.includes('502') || msg.includes('network') ||
      msg.includes('overload') || msg.includes('rate_limit');
    if (!isRetryable) throw err;
    console.warn('Stage failed, retrying once after delay:', msg);
    await new Promise(r => setTimeout(r, STAGE_RETRY_DELAY_MS));
    if (signal?.aborted) throw err;
    return await fn();
  }
}

export async function performTenancyReviewAndRewrite(
  documentText: string,
  options?: TenancyReviewOptions,
): Promise<TenancyReviewResult> {
  const { onProgress, previousAnalysis, signal } = options || {};
  let analysis: TenancyAnalysis;

  if (previousAnalysis) {
    analysis = previousAnalysis;
    onProgress?.({
      status: 'analysing',
      stage: 1,
      totalStages: 4,
      message: 'Using previous analysis results',
      percent: 25,
    });
  } else {
    onProgress?.({
      status: 'analysing',
      stage: 1,
      totalStages: 4,
      message: 'Checking legal compliance and identifying issues',
      percent: 2,
    });

    const analysisResult = await retryStage(async () => {
      let analysisChars = 0;
      return callStreamingEdgeFunction(
        {
          action: 'tenancy-review-rewrite',
          systemPrompt: ANALYSIS_PROMPT,
          userPrompt: `Analyse this tenancy agreement:\n\n${documentText}`,
          maxTokens: 4000,
          temperature: 0.1,
          model: 'claude-sonnet-4-6',
        },
        (chunk) => {
          analysisChars += chunk.length;
          const ratio = Math.min(analysisChars / 3000, 1);
          onProgress?.({
            status: 'analysing',
            stage: 1,
            totalStages: 4,
            message: 'Checking legal compliance and identifying issues',
            percent: Math.round(2 + ratio * 23),
            streamedChars: analysisChars,
          });
        },
        signal,
        {
          onRetry: () => onProgress?.({
            status: 'analysing', stage: 1, totalStages: 4,
            message: 'Retrying analysis...', percent: 5,
          }),
        },
      );
    }, signal);

    analysis = parseJSON<TenancyAnalysis>(analysisResult.content, DEFAULT_ANALYSIS);
  }

  onProgress?.({
    status: 'analysing',
    stage: 1,
    totalStages: 4,
    message: `Found ${analysis.issueCount} issues - compliance score: ${analysis.complianceScore}%`,
    percent: 25,
  });

  onProgress?.({
    status: 'rewriting',
    stage: 2,
    totalStages: 4,
    message: 'Creating your compliant legal document',
    percent: 26,
  });

  const rewriteDocText = truncateForRewrite(documentText);
  const rewriteResult = await retryStage(async () => {
    return rewriteWithContinuation({
      baseRequest: {
        action: 'tenancy-review-rewrite',
        systemPrompt: REWRITE_PROMPT,
        userPrompt: `Here is the original tenancy agreement to rewrite:\n\n${rewriteDocText}\n\n---\n\nHere is the compliance analysis:\n\n${JSON.stringify(analysis)}`,
        maxTokens: 64000,
        temperature: 0.15,
        model: 'claude-sonnet-4-6',
      },
      onChunk: () => {},
      onProgress,
      signal,
      streamingOptions: {
        onRetry: () => onProgress?.({
          status: 'rewriting', stage: 2, totalStages: 4,
          message: 'Retrying document rewrite...', percent: 30,
        }),
      },
      progressMessage: 'Creating your compliant legal document',
      documentType: 'tenancy',
    });
  }, signal);

  const rewrittenDocument = regenerateTableOfContents(
    removeDuplicateSections(rewriteResult.content),
  );

  onProgress?.({
    status: 'rewriting',
    stage: 2,
    totalStages: 4,
    message: 'Document rewrite complete',
    percent: 75,
  });

  onProgress?.({
    status: 'annotating',
    stage: 3,
    totalStages: 4,
    message: 'Comparing changes and documenting reasons',
    percent: 76,
  });

  const { original: annOriginal, rewritten: annRewritten } = truncateForAnnotation(documentText, rewrittenDocument);
  const annotationsResult = await retryStage(async () => {
    let annotationChars = 0;
    return callStreamingEdgeFunction(
      {
        action: 'tenancy-review-rewrite',
        systemPrompt: ANNOTATIONS_PROMPT,
        userPrompt: `ORIGINAL DOCUMENT:\n\n${annOriginal}\n\n---\n\nREWRITTEN DOCUMENT:\n\n${annRewritten}`,
        maxTokens: 8000,
        temperature: 0.1,
        model: 'claude-sonnet-4-6',
      },
      (chunk) => {
        annotationChars += chunk.length;
        const ratio = Math.min(annotationChars / 6000, 1);
        onProgress?.({
          status: 'annotating',
          stage: 3,
          totalStages: 4,
          message: 'Comparing changes and documenting reasons',
          percent: Math.round(76 + ratio * 19),
          streamedChars: annotationChars,
        });
      },
      signal,
      {
        onRetry: () => onProgress?.({
          status: 'annotating', stage: 3, totalStages: 4,
          message: 'Retrying annotation generation...', percent: 78,
        }),
      },
    );
  }, signal);

  const annotations = parseJSON<Annotation[]>(annotationsResult.content, []);

  onProgress?.({
    status: 'finalising',
    stage: 4,
    totalStages: 4,
    message: 'Assembling your complete review package',
    percent: 97,
  });

  onProgress?.({
    status: 'complete',
    stage: 4,
    totalStages: 4,
    message: 'Review complete',
    percent: 100,
  });

  return {
    analysis,
    rewrittenDocument,
    annotations,
  };
}
